import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "@/pages/ForgotPassword";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { readError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pw !== pw2) {
      setError("Passwords do not match.");
      return;
    }
    if (!token) {
      setError("Missing or invalid reset token.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      navigate("/admin/login", { replace: true });
    } catch (err) {
      setError(readError(err, "Could not reset password."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Set a new password">
      <form onSubmit={submit} className="space-y-6" data-testid="reset-form">
        {!token && (
          <div className="text-sm rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
            Reset link is missing the token. Request a new one.
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="pw">New password</Label>
          <Input
            id="pw"
            type="password"
            autoComplete="new-password"
            required
            value={pw}
            disabled={busy}
            onChange={(e) => setPw(e.target.value)}
            placeholder="At least 8 characters"
            className="h-11 bg-card"
            data-testid="reset-password-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pw2">Confirm new password</Label>
          <Input
            id="pw2"
            type="password"
            autoComplete="new-password"
            required
            value={pw2}
            disabled={busy}
            onChange={(e) => setPw2(e.target.value)}
            className="h-11 bg-card"
            data-testid="reset-confirm-input"
          />
        </div>
        {error && (
          <div data-testid="reset-error" className="text-sm rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
            {error}
          </div>
        )}
        <Button type="submit" disabled={busy || !token} data-testid="reset-submit-btn" className="w-full h-11 shadow-interactive">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" strokeWidth={1.5} />}
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
      <div className="mt-6 text-center text-xs text-muted-foreground">
        <Link to="/admin/login" className="hover:text-primary transition-colors">
          ← Back to login
        </Link>
      </div>
    </AuthShell>
  );
}
